import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, Row, Col, Form, Spinner, Badge } from 'react-bootstrap';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from "@react-google-maps/api";
import axios from 'axios';
import { toast } from 'react-toastify';
import * as moment from 'moment';
import { decode } from "@googlemaps/polyline-codec";

import { DEV_SERVER } from '../../../constants/urls';
import { googleMapKey } from '../../../constants/googleMapKey';
import { userSignal } from '../../../signal/userSignal';
import {
    clusterTimelinePoints,
    formatTime,
    formatDuration,
    getBatteryColor,
    getBatteryTextColor
} from '../../../utilities/timelineHelpers';

import './TripTimeline.css';

const TripTimeline = ({ tripId, trip }) => {
    const [timeline, setTimeline] = useState([]);
    const [clusteredTimeline, setClusteredTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTimeline, setShowTimeline] = useState(true);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [locationName, setLocationName] = useState('');
    const [loadingLocation, setLoadingLocation] = useState(false);
    const [mapCenter, setMapCenter] = useState(null);
    const [mapZoom, setMapZoom] = useState(14);
    
    const timelineListRef = useRef(null);
    const mapRef = useRef(null);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: googleMapKey,
    });

    // Fetch timeline data
    useEffect(() => {
        const fetchTimeline = async () => {
            try {
                setLoading(true);
                const response = await axios.get(
                    `${DEV_SERVER}/trip/v2/getTripTimeline/${tripId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${userSignal.value?.token}`,
                        },
                        params: {
                            limit: 1000
                        }
                    }
                );

                if (response.data.status) {
                    const timelineData = response.data.data.timeline || [];
                    setTimeline(timelineData);
                    
                    // Cluster the timeline points
                    const clustered = clusterTimelinePoints(timelineData);
                    setClusteredTimeline(clustered);

                    // Set initial map center to first point or trip location
                    if (timelineData.length > 0) {
                        setMapCenter({
                            lat: timelineData[0].location[0],
                            lng: timelineData[0].location[1]
                        });
                    } else if (trip?.locationStartPoint) {
                        setMapCenter({
                            lat: trip.locationStartPoint[0],
                            lng: trip.locationStartPoint[1]
                        });
                    }
                } else {
                    toast.error(response.data.message || 'Failed to fetch timeline');
                }
            } catch (error) {
                console.error('Error fetching timeline:', error);
                toast.error('Failed to load timeline data');
            } finally {
                setLoading(false);
            }
        };

        if (tripId) {
            fetchTimeline();
        }
    }, [tripId]);

    // Fetch location name using Google Geocoding API
    const fetchLocationName = async (lat, lng) => {
        try {
            setLoadingLocation(true);
            const response = await axios.get(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapKey}`
            );

            if (response.data.results && response.data.results.length > 0) {
                const result = response.data.results[0];
                let area = '';
                let city = '';

                for (const component of result.address_components) {
                    if (component.types.includes('sublocality') ||
                        component.types.includes('sublocality_level_1')) {
                        area = component.long_name;
                    }
                    if (component.types.includes('locality')) {
                        city = component.long_name;
                    }
                }

                const locationText = area && city
                    ? `${area}, ${city}`
                    : result.formatted_address.split(',').slice(0, 2).join(',');

                setLocationName(locationText);
            }
        } catch (error) {
            console.error('Error fetching location:', error);
            setLocationName('Unable to fetch location');
        } finally {
            setLoadingLocation(false);
        }
    };

    // Handle point selection
    const handlePointClick = (point, index) => {
        setSelectedPoint({ ...point, index });
        
        // Get coordinates based on point type
        const coords = point.type === 'cluster' 
            ? point.location 
            : point.point.location;

        // Center map on selected point with animation
        if (mapRef.current) {
            setMapCenter({ lat: coords[0], lng: coords[1] });
            setMapZoom(16);
        }

        // Fetch location name
        fetchLocationName(coords[0], coords[1]);

        // Scroll timeline card into view
        setTimeout(() => {
            const card = document.getElementById(`timeline-card-${index}`);
            if (card && timelineListRef.current) {
                card.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };

    // Handle marker click
    const handleMarkerClick = (point, index) => {
        handlePointClick(point, index);
    };

    // Create timeline polyline path
    const timelinePath = useMemo(() => {
        return timeline.map(point => ({
            lat: point.location[0],
            lng: point.location[1]
        }));
    }, [timeline]);

    // Decode trip path
    const tripPath = useMemo(() => {
        if (trip?.locationPath) {
            return decode(trip.locationPath).map(l => ({ lat: l[0], lng: l[1] }));
        }
        return [];
    }, [trip?.locationPath]);

    // Simple explicit icons for trip start/end (use trip api coordinates directly)
    const startEndIcons = useMemo(() => {
        if (typeof window === 'undefined' || !window.google) return {};

        const makeIcon = (label, bgColor, glowColor, size = 52) => {
            const svg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="${size + 8}" height="${size + 8}" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <filter id="shadow-${label}" x="-50%" y="-50%" width="200%" height="200%">
                            <feDropShadow dx="0" dy="0" stdDeviation="3" flood-color="${glowColor}" flood-opacity="0.8"/>
                        </filter>
                    </defs>
                    <circle cx="${(size + 8) / 2}" cy="${(size + 8) / 2}" r="${size / 2 + 2}" fill="${glowColor}" opacity="0.28"/>
                    <circle cx="${(size + 8) / 2}" cy="${(size + 8) / 2}" r="${size / 2}" fill="${bgColor}" stroke="white" stroke-width="4" filter="url(#shadow-${label})"/>
                    <text x="${(size + 8) / 2}" y="${(size + 8) / 2 + 6}" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="bold" fill="white">${label}</text>
                </svg>
            `)}`;
            return {
                url: svg,
                size: new window.google.maps.Size(size + 8, size + 8),
                anchor: new window.google.maps.Point((size + 8) / 2, (size + 8) / 2)
            };
        };

        return {
            start: makeIcon('S', '#10B981', '#10B981'),
            end: makeIcon('E', '#EF4444', '#EF4444')
        };
    }, [trip?.locationStartPoint, trip?.locationEndPoint]);

    // Render marker for timeline point
    const renderMarker = (point, index) => {
        const isSelected = selectedPoint?.index === index;
        const coords = point.type === 'cluster' ? point.location : point.point.location;
        
        // Determine if this is start or end based on coordinate matching
        let isStart = false;
        let isEnd = false;
        
        // Helper function to check if two coordinates match (with small tolerance for floating point)
        const coordinatesMatch = (coord1, coord2, tolerance = 0.0001) => {
            if (!coord1 || !coord2) return false;
            return Math.abs(coord1[0] - coord2[0]) < tolerance && 
                   Math.abs(coord1[1] - coord2[1]) < tolerance;
        };
        
        // Check if this point matches the start or end location from trip data
        if (trip?.locationStartPoint && coordinatesMatch(coords, trip.locationStartPoint)) {
            isStart = true;
        }
        if (trip?.locationEndPoint && coordinatesMatch(coords, trip.locationEndPoint)) {
            isEnd = true;
        }

        // Determine marker color and size with higher priority for start/end
        let backgroundColor = '#2563EB'; // Blue for intermediate
        let strokeColor = 'white';
        let strokeWidth = 2;
        
        if (isStart) {
            backgroundColor = '#10B981'; // Emerald green for start
            strokeColor = 'white';
            strokeWidth = 4;
        }
        if (isEnd) {
            backgroundColor = '#EF4444'; // Bright red for end
            strokeColor = 'white';
            strokeWidth = 4;
        }
        if (point.type === 'cluster' && !isStart && !isEnd) {
            backgroundColor = '#F59E0B'; // Amber for clusters
        }
        if (isSelected && !isStart && !isEnd) {
            backgroundColor = '#8B5CF6'; // Purple for selected
        }

        // Make start and end markers larger and more prominent
        const size = isStart || isEnd ? 52 : (isSelected ? 44 : (point.type === 'cluster' ? 36 : 28));
        
        // Set z-index: start and end should always be on top (10000+), selected next (9999), then regular markers
        let zIndex = index + 100;
        if (isSelected && !isStart && !isEnd) zIndex = 9999;
        if (isStart) zIndex = 10001; // Start always on top
        if (isEnd) zIndex = 10000; // End just below start

        // Create custom marker SVG with enhanced styling for start/end
        let markerSvg;
        
        if (isStart) {
            // Special design for start marker with outer glow
            markerSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="${size + 8}" height="${size + 8}" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <filter id="shadow-start" x="-50%" y="-50%" width="200%" height="200%">
                            <feDropShadow dx="0" dy="0" stdDeviation="3" flood-color="#10B981" flood-opacity="0.8"/>
                        </filter>
                    </defs>
                    <circle cx="${(size + 8) / 2}" cy="${(size + 8) / 2}" r="${size / 2 + 2}" fill="#10B981" opacity="0.3"/>
                    <circle cx="${(size + 8) / 2}" cy="${(size + 8) / 2}" r="${size / 2}" fill="${backgroundColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" filter="url(#shadow-start)"/>
                    <text x="${(size + 8) / 2}" y="${(size + 8) / 2 + 6}" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="bold" fill="white">S</text>
                </svg>
            `)}`;
        } else if (isEnd) {
            // Special design for end marker with outer glow
            markerSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="${size + 8}" height="${size + 8}" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <filter id="shadow-end" x="-50%" y="-50%" width="200%" height="200%">
                            <feDropShadow dx="0" dy="0" stdDeviation="3" flood-color="#EF4444" flood-opacity="0.8"/>
                        </filter>
                    </defs>
                    <circle cx="${(size + 8) / 2}" cy="${(size + 8) / 2}" r="${size / 2 + 2}" fill="#EF4444" opacity="0.3"/>
                    <circle cx="${(size + 8) / 2}" cy="${(size + 8) / 2}" r="${size / 2}" fill="${backgroundColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" filter="url(#shadow-end)"/>
                    <text x="${(size + 8) / 2}" y="${(size + 8) / 2 + 6}" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="bold" fill="white">E</text>
                </svg>
            `)}`;
        } else if (point.type === 'cluster') {
            // Cluster marker
            markerSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="white" stroke="${backgroundColor}" stroke-width="4"/>
                    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 8}" fill="${backgroundColor}"/>
                    <text x="${size / 2}" y="${size / 2 + 5}" text-anchor="middle" font-family="Arial, sans-serif" font-size="14" font-weight="bold" fill="white">${index + 1}</text>
                </svg>
            `)}`;
        } else {
            // Regular intermediate marker
            markerSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${backgroundColor}" stroke="${strokeColor}" stroke-width="2"/>
                    <text x="${size / 2}" y="${size / 2 + 5}" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="white">${index + 1}</text>
                </svg>
            `)}`;
        }

        const markerSize = (isStart || isEnd) ? size + 8 : size;

        return (
            <Marker
                key={`marker-${index}`}
                position={{ lat: coords[0], lng: coords[1] }}
                onClick={() => handleMarkerClick(point, index)}
                icon={{
                    url: markerSvg,
                    scaledSize: new window.google.maps.Size(markerSize, markerSize),
                    anchor: new window.google.maps.Point(markerSize / 2, markerSize / 2)
                }}
                zIndex={zIndex}
            />
        );
    };

    // Render timeline card
    const renderTimelineCard = (point, index) => {
        const isSelected = selectedPoint?.index === index;
        const nextPoint = clusteredTimeline[index + 1];

        // Determine if this is start or end based on coordinate matching
        let isStart = false;
        let isEnd = false;
        
        // Helper function to check if two coordinates match (with small tolerance for floating point)
        const coordinatesMatch = (coord1, coord2, tolerance = 0.0001) => {
            if (!coord1 || !coord2) return false;
            return Math.abs(coord1[0] - coord2[0]) < tolerance && 
                   Math.abs(coord1[1] - coord2[1]) < tolerance;
        };
        
        // Get coordinates based on point type
        const coords = point.type === 'cluster' ? point.location : point.point.location;
        
        // Check if this point matches the start or end location from trip data
        if (trip?.locationStartPoint && coordinatesMatch(coords, trip.locationStartPoint)) {
            isStart = true;
        }
        if (trip?.locationEndPoint && coordinatesMatch(coords, trip.locationEndPoint)) {
            isEnd = true;
        }

        if (point.type === 'cluster') {
            const duration = point.endTime - point.startTime;
            const hours = Math.floor(duration / (1000 * 60 * 60));
            const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));

            return (
                <div
                    key={`card-${index}`}
                    id={`timeline-card-${index}`}
                    className={`timeline-card ${isSelected ? 'timeline-card-selected' : ''}`}
                    onClick={() => handlePointClick(point, index)}
                >
                    <div className="timeline-card-header">
                        <div className="timeline-badge timeline-badge-cluster">
                            {index + 1}
                        </div>
                        <div className="timeline-card-time">
                            <div className="timeline-time-main">
                                {formatTime(point.startTime)} → {formatTime(point.endTime)}
                            </div>
                            <div className="timeline-stop-badge">Stop</div>
                        </div>
                    </div>
                    <div className="timeline-card-body">
                        <div className="timeline-duration-stop">
                            Stopped for {hours > 0 ? `${hours}h ` : ''}{minutes}m
                        </div>
                        <div className="timeline-battery-row">
                            <span>Start: {point.startBattery}%</span>
                            <span>End: {point.endBattery}%</span>
                        </div>
                        <div className="timeline-coordinates">
                            {point.location[0].toFixed(5)}, {point.location[1].toFixed(5)}
                        </div>
                        {isSelected && locationName && (
                            <div className="timeline-location-name">
                                <i className="fas fa-map-marker-alt me-2"></i>
                                {loadingLocation ? 'Loading...' : locationName}
                            </div>
                        )}
                        <div className="timeline-distance">
                            Distance so far: {point.distanceSoFar.toFixed(2)} km
                        </div>
                    </div>
                </div>
            );
        } else {
            const p = point.point;
            const batteryBg = getBatteryColor(p.battery);
            const batteryTextColor = getBatteryTextColor(p.battery);

            return (
                <div
                    key={`card-${index}`}
                    id={`timeline-card-${index}`}
                    className={`timeline-card ${isSelected ? 'timeline-card-selected' : ''} ${isStart ? 'timeline-card-start' : ''} ${isEnd ? 'timeline-card-end' : ''}`}
                    onClick={() => handlePointClick(point, index)}
                >
                    <div className="timeline-card-header">
                        <div className={`timeline-badge ${isStart ? 'timeline-badge-start' : (isEnd ? 'timeline-badge-end' : 'timeline-badge-intermediate')}`}>
                            {index + 1}
                        </div>
                        <div className="timeline-card-time">
                            <div className="timeline-time-main">{formatTime(p.locationTimestamp)}</div>
                            {nextPoint && (
                                <div className="timeline-duration">
                                    {formatDuration(p.locationTimestamp, 
                                        nextPoint.type === 'cluster' ? nextPoint.startTime : nextPoint.point.locationTimestamp
                                    )} → {formatTime(
                                        nextPoint.type === 'cluster' ? nextPoint.startTime : nextPoint.point.locationTimestamp
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="timeline-card-body">
                        <div 
                            className="timeline-battery"
                            style={{ 
                                backgroundColor: batteryBg,
                                color: batteryTextColor
                            }}
                        >
                            Battery: {p.battery}%
                        </div>
                        <div className="timeline-coordinates">
                            {p.location[0].toFixed(5)}, {p.location[1].toFixed(5)}
                        </div>
                        {isSelected && locationName && (
                            <div className="timeline-location-name">
                                <i className="fas fa-map-marker-alt me-2"></i>
                                {loadingLocation ? 'Loading...' : locationName}
                            </div>
                        )}
                        <div className="timeline-distance">
                            Distance so far: {p.distanceSoFar.toFixed(2)} km
                        </div>
                        {isStart && <Badge bg="success" className="mt-2">Start</Badge>}
                        {isEnd && <Badge bg="danger" className="mt-2">End</Badge>}
                    </div>
                </div>
            );
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
                <span>Loading map...</span>
            </div>
        );
    }

    const rawPointCount = timeline.length;
    const displayPointCount = clusteredTimeline.length;

    return (
        <div className="trip-timeline-container">
            {/* Map Section */}
            <div className="timeline-map-section">
                <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    zoom={mapZoom}
                    center={mapCenter || { lat: 23.54891, lng: 72.5611 }}
                    options={{
                        streetViewControl: false,
                        zoomControl: true,
                        mapTypeControl: false,
                        fullscreenControl: true,
                    }}
                    onLoad={(map) => { mapRef.current = map; }}
                >
                    {/* Blue solid line - Actual trip path */}
                    {tripPath.length > 0 && (
                        <Polyline
                            path={tripPath}
                            options={{
                                geodesic: true,
                                strokeColor: '#2563EB',
                                strokeOpacity: 1,
                                strokeWeight: 4,
                                icons: [
                                    {
                                        icon: {
                                            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                            scale: 3,
                                            strokeColor: '#1E40AF',
                                            strokeWeight: 2,
                                            fillColor: '#FFFFFF',
                                            fillOpacity: 1,
                                        },
                                        offset: '0',
                                        repeat: '80px'
                                    }
                                ]
                            }}
                        />
                    )}

                    {/* Explicit start/end markers from trip data (always render above other markers) */}
                    {trip?.locationStartPoint && startEndIcons.start && (
                        <Marker
                            position={{ lat: trip.locationStartPoint[0], lng: trip.locationStartPoint[1] }}
                            icon={{ url: startEndIcons.start.url, scaledSize: startEndIcons.start.size, anchor: startEndIcons.start.anchor }}
                            zIndex={20000}
                        />
                    )}
                    {trip?.locationEndPoint && startEndIcons.end && (
                        <Marker
                            position={{ lat: trip.locationEndPoint[0], lng: trip.locationEndPoint[1] }}
                            icon={{ url: startEndIcons.end.url, scaledSize: startEndIcons.end.size, anchor: startEndIcons.end.anchor }}
                            zIndex={19999}
                        />
                    )}

                    {/* Timeline markers */}
                    {showTimeline && clusteredTimeline.map((point, index) => renderMarker(point, index))}
                </GoogleMap>

                {/* Legend */}
                {/* <div className="timeline-legend">
                    <div className="legend-item">
                        <div className="legend-line legend-line-solid"></div>
                        <span>Trip Path</span>
                    </div>
                </div> */}
            </div>

            {/* Timeline List Section */}
            <div className="timeline-list-section expanded">
                <div className="timeline-list-header">
                    <div className="timeline-list-title">
                        <h5 className="mb-0">Location Timeline</h5>
                        <div className="timeline-list-subtitle">
                            {moment(trip?.date).format('MMM DD, YYYY')} • {displayPointCount} points ({rawPointCount} raw)
                        </div>
                    </div>
                    <div className="timeline-toggle-container">
                        <span className="timeline-toggle-label">Show Markers on Map</span>
                        <Form.Check
                            type="switch"
                            id="timeline-toggle"
                            checked={showTimeline}
                            onChange={(e) => setShowTimeline(e.target.checked)}
                        />
                    </div>
                </div>

                <div className="timeline-list-content" ref={timelineListRef}>
                    {clusteredTimeline.length === 0 ? (
                        <div className="text-center py-4">
                            <span>No timeline data available</span>
                        </div>
                    ) : (
                        <div className="timeline-cards-grid">
                            {clusteredTimeline.map((point, index) => renderTimelineCard(point, index))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TripTimeline;
